import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DeletionWarningModal } from './DeletionWarningModal';
import { FinalConfirmationModal } from './FinalConfirmationModal';
import { validateAccountDeletion, deleteBuyerAccount, deleteSellerAccount } from '@/services/accountDeletion';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: 'buyer' | 'seller';
  userData: {
    id: string;
    email: string;
    name: string;
  } | null;
}

type DeletionStep = 'warning' | 'final_confirm' | 'processing' | 'completed';

export function AccountDeletionModal({ 
  isOpen, 
  onClose, 
  userRole,
  userData 
}: AccountDeletionModalProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [currentStep, setCurrentStep] = useState<DeletionStep>('warning');
  const [isValidating, setIsValidating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('warning');
      setIsValidating(false);
      setIsDeleting(false);
      setValidationError('');
    }
  }, [isOpen]);

  // 모달 외부 클릭 시 닫기 방지 (body overflow 제어)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFirstWarningConfirm = async () => {
    if (!userData?.id) return;

    setIsValidating(true);
    setValidationError('');

    try {
      const validation = await validateAccountDeletion(userData.id, userRole);
      
      if (validation.success && validation.data) {
        if (validation.data.canDelete) {
          setCurrentStep('final_confirm');
        } else {
          setValidationError(validation.data.reason || '계정을 삭제할 수 없습니다.');
        }
      } else {
        setValidationError(validation.error || '계정 삭제 가능 여부 확인에 실패했습니다.');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidationError('계정 삭제 가능 여부 확인 중 오류가 발생했습니다.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleFinalConfirm = async () => {
    if (!userData?.id) return;

    setIsDeleting(true);
    setCurrentStep('processing');

    try {
      let result;
      
      if (userRole === 'buyer') {
        result = await deleteBuyerAccount(userData.id);
      } else {
        result = await deleteSellerAccount(userData.id);
      }

      if (result.success) {
        setCurrentStep('completed');
        
        // 로그아웃 및 홈페이지로 리다이렉트
        setTimeout(async () => {
          await signOut();
          navigate('/', { replace: true });
        }, 3000);
      } else {
        setValidationError(result.error || '계정 삭제에 실패했습니다.');
        setCurrentStep('final_confirm');
      }
    } catch (error) {
      console.error('Deletion error:', error);
      setValidationError('계정 삭제 중 오류가 발생했습니다.');
      setCurrentStep('final_confirm');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (currentStep !== 'processing' && currentStep !== 'completed') {
      onClose();
    }
  };

  const handleBackToWarning = () => {
    setCurrentStep('warning');
    setValidationError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        className="relative bg-white rounded-xl w-full max-w-[400px] lg:max-w-[600px] shadow-xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'warning' && (
            <DeletionWarningModal
              userRole={userRole}
              userName={userData?.name || ''}
              onConfirm={handleFirstWarningConfirm}
              onCancel={handleClose}
              isValidating={isValidating}
              validationError={validationError}
            />
          )}

          {currentStep === 'final_confirm' && (
            <FinalConfirmationModal
              userRole={userRole}
              userData={userData}
              onConfirm={handleFinalConfirm}
              onCancel={handleClose}
              onBack={handleBackToWarning}
              isDeleting={isDeleting}
              error={validationError}
            />
          )}

          {currentStep === 'processing' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto mb-4"></div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                계정을 삭제하는 중...
              </h4>
              <p className="text-sm text-gray-600">
                잠시만 기다려주세요. 이 과정은 몇 분 정도 소요될 수 있습니다.
              </p>
            </div>
          )}

          {currentStep === 'completed' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                계정이 삭제되었습니다
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                {userData?.name || '사용자'}님의 계정이 성공적으로 삭제되었습니다.
              </p>
              <p className="text-xs text-gray-500">
                3초 후 홈페이지로 이동합니다...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}